import React from 'react';
import { Briefcase, Sparkles, Users, Unlock } from 'lucide-react';

interface Step {
  icon: typeof Briefcase;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    icon: Briefcase,
    title: 'Post Your Job',
    description: 'Describe the role and ideal candidate. Our AI processes it semantically.',
  },
  {
    icon: Sparkles,
    title: 'AI Ranks Candidates',
    description: 'Our software semantically ranks candidates by relevance.',
  },
  {
    icon: Users,
    title: 'Review & Shortlist',
    description: 'Browse profiles, filter by education and experience.',
  },
  {
    icon: Unlock,
    title: 'Connect',
    description: 'Download CVs, reach out directly.',
  },
];

export const HowItWorksSection: React.FC = () => {
  return (
    <section
      id="how-it-works"
      className="w-full py-16 md:py-20 px-4 md:px-6 scroll-mt-24 bg-slate-50/50"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
            How It Works
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            From job post to perfect hire in 4 steps
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Our semantic matching technology does the heavy lifting so you can focus on finding the right fit.
          </p>
        </div>

        {/* Steps Grid - Desktop */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-6 relative">
          {/* Connecting line */}
          <div className="absolute top-14 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-slate-200 via-blue-200 to-slate-200" />

          {steps.map((step, index) => (
            <div
              key={step.title}
              className="relative flex flex-col items-center text-center"
            >
              {/* Step icon */}
              <div className="relative z-10 mb-6">
                <div className="w-24 h-24 rounded-2xl bg-white border border-slate-100 shadow-md flex items-center justify-center hover:shadow-lg hover:border-blue-100 transition-all duration-200">
                  <step.icon className="w-9 h-9 text-blue-600" />
                </div>
                {/* Step number badge */}
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {index + 1}
                </div>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed max-w-[200px]">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Steps Grid - Mobile/Tablet */}
        <div className="lg:hidden space-y-5">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="relative flex gap-4 items-start"
            >
              {/* Timeline line */}
              {index < steps.length - 1 && (
                <div className="absolute left-7 top-16 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-slate-200" />
              )}

              {/* Step icon */}
              <div className="relative z-10 flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center">
                  <step.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-xs shadow">
                  {index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 pt-1 pb-4">
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
